/** A small panel under its button, drawn above the panes (a pane clips
 *  what overflows it) and kept inside the window. Esc is the caller's;
 *  a click outside it or its button closes it. Place it inside the
 *  element it hangs from. */
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { listenForDismiss } from "../lib/dismiss";

export function FloatingPopover({ children, onClose, align = "left", width = 300 }: { children: ReactNode; onClose: () => void; align?: "left" | "right"; width?: number }) {
  const markerRef = useRef<HTMLSpanElement>(null);
  const ref = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  useLayoutEffect(() => {
    const place = () => {
      const anchor = markerRef.current?.parentElement;
      if (!anchor) return;
      const r = anchor.getBoundingClientRect();
      const want = align === "right" ? r.right - width : r.left;
      setPos({ left: Math.max(8, Math.min(want, window.innerWidth - width - 8)), top: r.bottom + 6 });
    };
    place();
    window.addEventListener("resize", place);
    return () => window.removeEventListener("resize", place);
  }, [align, width]);
  useEffect(
    () => listenForDismiss((t) => !!ref.current?.contains(t) || !!markerRef.current?.parentElement?.contains(t), () => closeRef.current()),
    [],
  );
  return (
    <>
      <span ref={markerRef} hidden />
      {pos &&
        createPortal(
          <div
            ref={ref}
            style={{ width, left: pos.left, top: pos.top, position: "fixed" }}
            className="wov-rise z-[95] max-h-[70vh] overflow-y-auto rounded-xl bg-white p-2 shadow-2xl ring-1 ring-slate-200 dark:bg-slate-800 dark:ring-slate-700"
          >
            {children}
          </div>,
          document.body,
        )}
    </>
  );
}
